import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetUpdatedProfileRequestPayload,
  GetUserProfileRequestPayload,
  UserState,
} from './user-profile.payload';
import { UpdateUserProfilePhotoRequest } from '../../atlasguides-web-common/src/functions/users/update-user-profile-photo';
import { GuidePurchaseReceipt, UserPrimaryInfo, UserProfileWithPurchases } from '../../atlasguides-web-common/src/functions/users/dto/user-profile.dto';
import { GetPlannedRoutesRequest, PlannedRoute } from 'atlasguides-web-common/src/functions/users/get-planned-routes';
import { getEnabledPlannedRoute, setEnabledPlannedRoute as setPlannedRouteEnabled, updatePlannedRouteInfoByTrail } from 'utils/plannedRoutesHelper';
import { PlannedRouteTrail } from 'atlasguides-web-common/src/functions/guides/dto/guide-geo.dto';
import { PlannedRouteExt } from 'commonTypes/GuideUse/guideGeo';
export * from './user-profile.payload';

const initialState: UserState = {
  isLoading: false,
  userProfile: null,
  isUploadingProfileImage: false,
  isUploadingCoverImage: false,
  plannedRoutes: null,
  isLoadingPlannedRoutes: false,
  isSignedIn: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUserProfileRequest: (
      state,
      action: PayloadAction<GetUserProfileRequestPayload>
    ) => {
      state.isLoading = true;
    },
    getUserProfileResponse: (
      state,
      action: PayloadAction<UserProfileWithPurchases>
    ) => {
      state.userProfile = action.payload;
      state.isLoading = false;
    },
    getUserProfileFail: (state, action: PayloadAction<void>) => {
      state.isLoading = false;
    },
    clearUserProfile: (state, action: PayloadAction<void>) => {
      state.userProfile = null;
      state.plannedRoutes = null;
      state.isLoading = false;
      state.isUploadingProfileImage = false;
      state.isUploadingCoverImage = false;
      state.isLoadingPlannedRoutes = false;
    },
    getUpdatedProfileRequest: (
      state,
      action: PayloadAction<GetUpdatedProfileRequestPayload>
    ) => state,
    getUpdatedProfileResponse: (
      state,
      action: PayloadAction<Partial<UserPrimaryInfo>>
    ) => {
      if (state.userProfile) {
        state.userProfile.primaryInfo = {
          ...state.userProfile.primaryInfo,
          ...action.payload,
        };
      }
      state.isLoading = false;
    },
    updateUserImageProfileRequest: (
      state,
      action: PayloadAction<UpdateUserProfilePhotoRequest>
    ) => {
      state.isUploadingProfileImage = true;
    },
    updateUserImageProfile: (
      state,
      action: PayloadAction<{ imageUrl: string }>
    ) => {
      if (state.userProfile) {
        state.userProfile.photos.profilePhotoUrl = action.payload.imageUrl;
        state.isUploadingProfileImage = false;
      }
    },
    updateUserImageProfileFail: (state, action: PayloadAction<void>) => {
      state.isUploadingProfileImage = false;
    },
    updateUserCoverImageRequest: (
      state,
      action: PayloadAction<UpdateUserProfilePhotoRequest>
    ) => {
      state.isUploadingCoverImage = true;
    },
    updateUserCoverImage: (
      state,
      action: PayloadAction<{ imageUrl: string }>
    ) => {
      if (state.userProfile) {
        state.userProfile.photos.profileCoverImageUrl = action.payload.imageUrl;
        state.isUploadingCoverImage = false;
      }
    },
    updateUserCoverImageFail: (state, action: PayloadAction<void>) => {
      state.isUploadingCoverImage = false;
    },
    addPurchasedItems: (
      state,
      action: PayloadAction<{ purchasedItems: GuidePurchaseReceipt[] }>
    ) => {
      if (state.userProfile) {
        state.userProfile.purchases = [
          ...action.payload.purchasedItems,
          ...(state.userProfile?.purchases || []),
        ];
      }
    },
    getPlannedRouteRequest: (
      state,
      action: PayloadAction<GetPlannedRoutesRequest>
    ) => {
      state.isLoadingPlannedRoutes = true;
    },
    getPlannedRoutesResponse: (
      state,
      action: PayloadAction<PlannedRoute[]>
    ) => {

      // Copy to mutate objects
      state.plannedRoutes = action.payload.map(item => ({
        ...item,
        trail: null,
        isEnabled: false
      }));

      // Initialize enabled planned route
      const enabledPlannedRoute = getEnabledPlannedRoute(state.plannedRoutes);
      if (enabledPlannedRoute != null) {
        enabledPlannedRoute.isEnabled = true;
      }

      state.isLoadingPlannedRoutes = false;
    },
    setPlannedRouteEnabled: (
      state,
      action: PayloadAction<{ plannedRoute: PlannedRouteExt, isEnabled: boolean }>
    ) => {
      if (state.plannedRoutes !== null) {
        setPlannedRouteEnabled(action.payload.plannedRoute, state.plannedRoutes, action.payload.isEnabled)
      }
    },
    updatePlannedRouteInfo: (
      state,
      action: PayloadAction<{ plannedRoute: PlannedRouteExt, trail: PlannedRouteTrail }>
    ) => {
      if (state.plannedRoutes !== null) {
        updatePlannedRouteInfoByTrail(action.payload.plannedRoute, state.plannedRoutes, action.payload.trail)
      }
    },
  },
});

export const userActions = userSlice.actions;
export const userReducer = userSlice.reducer;


